import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Refresh,
  ReceiptLong,
  Payments,
  Groups,
  AccountBalance,
  WarningAmber,
  MoreVert,
  FileDownload,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import {
  BillingPage,
  BillingPanel,
  BillingStat,
  BillingTableHead,
  StatusChip,
  btnGhost,
  btnPrimary,
  billingSx,
  tones,
} from './billingUi';
import { fetchBillingDashboard, downloadInvoicesCsv, formatMoney } from '../../services/billingApi';
import { formatAppDate } from '../../services/appConfigRuntime';

const moneyOf = (row, snake, camel) => row[snake] ?? row[camel];

const rowRank = (row) => {
  const invoice = String(row.invoice_status || row.invoiceStatus || '').toUpperCase();
  const status = String(row.subscription_status || row.subscriptionStatus || '').toUpperCase();
  const due = Number(moneyOf(row, 'outstanding_balance', 'outstandingBalance') || 0);
  if (invoice === 'OVERDUE' || status === 'GRACE_PERIOD') return 0;
  if (due > 0) return 1;
  if (status === 'SUSPENDED') return 2;
  return 3;
};

/** Spec §8.1 Billing Dashboard */
const BillingDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [menu, setMenu] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchBillingDashboard());
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tenants = useMemo(() => {
    const rows = (data?.tenants || []).filter((row) => {
      const name = String(row.tenant_name || row.tenantName || '').toLowerCase();
      const status = String(row.subscription_status || row.subscriptionStatus || '').toUpperCase();
      const renewal = String(row.renewal_date || row.renewalDate || '');
      const invoiceStatus = String(row.invoice_status || row.invoiceStatus || '').toUpperCase();
      if (tenantFilter && !name.includes(tenantFilter.toLowerCase())) return false;
      if (statusFilter && status !== statusFilter) return false;
      if (periodFilter && !renewal.startsWith(periodFilter)) return false;
      if (invoiceFilter && invoiceStatus !== invoiceFilter) return false;
      return true;
    });
    return rows.sort((a, b) => {
      const rank = rowRank(a) - rowRank(b);
      if (rank !== 0) return rank;
      return String(a.tenant_name || a.tenantName || '').localeCompare(
        String(b.tenant_name || b.tenantName || '')
      );
    });
  }, [data, tenantFilter, statusFilter, periodFilter, invoiceFilter]);

  const filtersOn = Boolean(tenantFilter || statusFilter || periodFilter || invoiceFilter);
  const clearFilters = () => {
    setTenantFilter('');
    setStatusFilter('');
    setPeriodFilter('');
    setInvoiceFilter('');
  };

  return (
    <BillingPage>
      <BillingNav
        title="Billing Dashboard"
        subtitle="Who owes money, what renews next, and where to act."
        actions={
          <>
            <Button startIcon={<Refresh />} variant="outlined" sx={btnGhost} onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<ReceiptLong />}
              sx={btnPrimary}
              onClick={() => navigate('/admin/billing/invoices')}
            >
              New invoice
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            {[
              {
                tone: 'teal',
                icon: <Groups />,
                label: 'Active subscriptions',
                value: data?.activeSubscriptions ?? 0,
                onClick: () => setStatusFilter((s) => (s === 'ACTIVE' ? '' : 'ACTIVE')),
                selected: statusFilter === 'ACTIVE',
              },
              {
                tone: 'blue',
                icon: <ReceiptLong />,
                label: 'Issued this period',
                value: formatMoney(data?.issuedThisPeriod),
              },
              {
                tone: 'amber',
                icon: <AccountBalance />,
                label: 'Outstanding',
                value: formatMoney(data?.outstandingAmount),
              },
              {
                tone: 'rose',
                icon: <WarningAmber />,
                label: 'Overdue',
                value: formatMoney(data?.overdueAmount),
                onClick: () => setInvoiceFilter((s) => (s === 'OVERDUE' ? '' : 'OVERDUE')),
                selected: invoiceFilter === 'OVERDUE',
              },
            ].map((card) => (
              <Grid item xs={6} md={3} key={card.label}>
                <Box
                  onClick={card.onClick}
                  sx={{
                    cursor: card.onClick ? 'pointer' : 'default',
                    borderRadius: 2,
                    outline: card.selected ? `2px solid ${tones[card.tone].main}` : 'none',
                    outlineOffset: 1,
                  }}
                >
                  <BillingStat tone={card.tone} icon={card.icon} label={card.label} value={card.value} />
                </Box>
              </Grid>
            ))}
          </Grid>

          <BillingPanel tone="maroon">
            <Box
              sx={{
                ...billingSx.panelPad,
                py: 1.25,
                ...billingSx.toolbar,
                mb: 0,
                bgcolor: tones.maroon.soft,
                borderBottom: '1px solid',
                borderColor: tones.maroon.border,
              }}
            >
              <TextField
                size="small"
                placeholder="Search tenant"
                value={tenantFilter}
                onChange={(e) => setTenantFilter(e.target.value)}
                sx={{ minWidth: 180, bgcolor: 'background.paper' }}
              />
              <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All statuses</MenuItem>
                  {['ACTIVE', 'TRIAL', 'GRACE_PERIOD', 'SUSPENDED', 'EXPIRED', 'CANCELLED'].map((s) => (
                    <MenuItem key={s} value={s}>
                      {s.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="month"
                label="Renewal"
                InputLabelProps={{ shrink: true }}
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                sx={{ width: 160, bgcolor: 'background.paper' }}
              />
              <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'background.paper' }}>
                <InputLabel>Invoice</InputLabel>
                <Select label="Invoice" value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value)}>
                  <MenuItem value="">All invoices</MenuItem>
                  {['DRAFT', 'APPROVED', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'DISPUTED', 'VOID', 'WRITTEN_OFF'].map(
                    (s) => (
                      <MenuItem key={s} value={s}>
                        {s.replaceAll('_', ' ')}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {tenants.length} tenant{tenants.length === 1 ? '' : 's'}
              </Typography>
              {filtersOn && (
                <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none' }}>
                  Clear
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownload />}
                sx={btnGhost}
                onClick={() => downloadInvoicesCsv().catch((e) => toast.error(e.message))}
              >
                Export
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={btnGhost}
                onClick={() => navigate('/admin/billing/subscriptions')}
              >
                Add charge
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Payments />}
                sx={btnGhost}
                onClick={() => navigate('/admin/billing/payments')}
              >
                Payment
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)', minHeight: 280 }}>
              <Table size="small" stickyHeader>
                <BillingTableHead
                  columns={[
                    { label: 'Tenant' },
                    { label: 'Status' },
                    { label: 'Plan' },
                    { label: 'Renewal' },
                    { label: 'Recurring', align: 'right' },
                    { label: 'Outstanding', align: 'right' },
                    { label: 'Invoice' },
                    { label: '', align: 'right' },
                  ]}
                />
                <TableBody>
                  {tenants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 5, textAlign: 'center' }}>
                        <Typography color="text.secondary">No tenants match these filters.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tenants.map((row) => {
                      const tid = row.tenant_id || row.tenantId;
                      const status = row.subscription_status || row.subscriptionStatus;
                      const invoiceStatus = row.invoice_status || row.invoiceStatus;
                      const currency = row.billing_currency || row.billingCurrency || 'USD';
                      return (
                        <TableRow key={tid} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {row.tenant_name || row.tenantName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={status} />
                          </TableCell>
                          <TableCell>{row.license_plan_name || row.licensePlanName || '—'}</TableCell>
                          <TableCell>
                            {row.renewal_date || row.renewalDate
                              ? formatAppDate(row.renewal_date || row.renewalDate)
                              : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatMoney(moneyOf(row, 'estimated_recurring', 'estimatedRecurring'), currency)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                            {formatMoney(moneyOf(row, 'outstanding_balance', 'outstandingBalance'), currency)}
                          </TableCell>
                          <TableCell>{invoiceStatus ? <StatusChip status={invoiceStatus} /> : '—'}</TableCell>
                          <TableCell align="right" sx={{ width: 48 }}>
                            <IconButton
                              size="small"
                              aria-label="Open tenant billing"
                              onClick={(e) => setMenu({ anchor: e.currentTarget, tid })}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </BillingPanel>

          <Menu
            anchorEl={menu?.anchor}
            open={Boolean(menu)}
            onClose={() => setMenu(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {[
              ['Account', '/admin/billing/accounts'],
              ['Charges', '/admin/billing/subscriptions'],
              ['Invoice', '/admin/billing/invoices'],
              ['Usage', '/admin/billing/usage'],
              ['Payment', '/admin/billing/payments'],
              ['History', '/admin/billing/reports'],
            ].map(([label, path]) => (
              <MenuItem
                key={path}
                onClick={() => {
                  if (!menu) return;
                  const url =
                    label === 'History'
                      ? `${path}?tenantId=${menu.tid}&kind=tenant-history`
                      : `${path}?tenantId=${menu.tid}`;
                  setMenu(null);
                  navigate(url);
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </BillingPage>
  );
};

export default BillingDashboard;
